import React, { useState, useEffect } from "react";
import axios from "axios";
import "./App.css";

// UI PREVIEW MODE - Set to false for production
const USE_MOCK_DATA = false;

const MOCK_DATA = {
  fundTotal: 982500.50,
  ytdPerformance: 12.35,
  quarterlyFeePercent: 0.5,
  hurdleRate: 8,
  carriedInterest: 20,
  members: [
    { name: "John Smith", units: 50000 },
    { name: "Jane Doe", units: 30000 },
    { name: "Bob Johnson", units: 20000 },
  ]
};

const FundSettings = () => {
  const [members, setMembers] = useState([]);
  const [fundTotal, setFundTotal] = useState(0);
  const [ytdPerformance, setYtdPerformance] = useState(0);
  const [quarterlyFeePercent, setQuarterlyFeePercent] = useState(0);
  const [hurdleRate, setHurdleRate] = useState(0);
  const [carriedInterest, setCarriedInterest] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [message, setMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const API_BASE_URL = "https://fundtracker.reicheltcapital.com/api";

  useEffect(() => {
    fetchFundData();
  }, []);

  const fetchFundData = async () => {
    // Use mock data for UI preview
    if (USE_MOCK_DATA) {
      setTimeout(() => {
        setFundTotal(MOCK_DATA.fundTotal);
        setYtdPerformance(MOCK_DATA.ytdPerformance);
        setQuarterlyFeePercent(MOCK_DATA.quarterlyFeePercent);
        setHurdleRate(MOCK_DATA.hurdleRate);
        setCarriedInterest(MOCK_DATA.carriedInterest);
        setMembers(MOCK_DATA.members);
        setIsLoading(false);
      }, 500);
      return;
    }

    try {
      setIsLoading(true);
      const token = sessionStorage.getItem("authToken");
      if (!token) {
        setMessage("No authentication token found. Please log in.");
        setIsLoading(false);
        return;
      }

      const navResponse = await axios.get(`${API_BASE_URL}/compute-nav`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const liveNAV = navResponse.data.NAV || 0;
      setFundTotal(liveNAV);

      const historicalResponse = await axios.get(`${API_BASE_URL}/get-historical-data`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const historicalData = Array.isArray(historicalResponse.data) ? historicalResponse.data : [];

      const currentYear = new Date().getFullYear();
      const januaryData = historicalData
        .filter((entry) => {
          const entryDate = new Date(entry.date);
          return entryDate.getFullYear() === currentYear && entryDate.getMonth() === 0;
        })
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      const startingNAV = januaryData.length > 0 ? januaryData[0].nav : null;
      let calculatedYtdPerformance = 0;
      if (startingNAV && liveNAV && startingNAV !== 0) {
        calculatedYtdPerformance = ((liveNAV - startingNAV) / startingNAV) * 100;
      }
      setYtdPerformance(calculatedYtdPerformance);

      const snapshotResponse = await axios.get(`${API_BASE_URL}/get-latest-snapshot`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = snapshotResponse.data;

      if (data.message === "No data available.") {
        setMembers([]);
        setQuarterlyFeePercent(0);
        setHurdleRate(0);
        setCarriedInterest(0);
        setMessage("No previous fund settings found.");
        setIsLoading(false);
        return;
      }

      let membersParsed = typeof data.members === "string"
        ? JSON.parse(data.members.replace(/'/g, '"'))
        : data.members;
      setMembers(membersParsed);

      let fundParamsParsed = typeof data.fund_parameters === "string"
        ? JSON.parse(data.fund_parameters.replace(/'/g, '"'))
        : data.fund_parameters;
      setQuarterlyFeePercent(fundParamsParsed.quarterly_fee || 0);
      setHurdleRate(fundParamsParsed.hurdle_rate || 0);
      setCarriedInterest(fundParamsParsed.carried_interest || 0);
      setMessage("");
    } catch (error) {
      const errorDetail = error.response ? JSON.stringify(error.response.data) : error.message;
      setMessage(`Failed to load fund settings: ${errorDetail}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditClick = () => {
    const userConfirmed = window.confirm("Are you sure you want to edit the fund settings?");
    if (userConfirmed) {
      setIsEditing(true);
      setMessage("");
    }
  };

  const saveChanges = async () => {
    if (USE_MOCK_DATA) {
      setIsEditing(false);
      setMessage("Changes saved (preview mode)");
      return;
    }

    const userConfirmed = window.confirm("Are you sure you want to save these changes?");
    if (!userConfirmed) return;

    try {
      const token = sessionStorage.getItem("authToken");
      const updatedData = {
        nav: fundTotal,
        ytd_performance: ytdPerformance,
        members: members,
        fund_parameters: {
          quarterly_fee: quarterlyFeePercent,
          hurdle_rate: hurdleRate,
          carried_interest: carriedInterest,
        },
      };

      await axios.post(`${API_BASE_URL}/update-fund-settings`, updatedData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      setIsEditing(false);
      setMessage("Fund settings saved successfully!");
    } catch (error) {
      const errorDetail = error.response ? JSON.stringify(error.response.data) : error.message;
      setMessage(`Failed to save fund settings: ${errorDetail}`);
    }
  };

  const addMember = () => {
    setMembers([...members, { name: "", units: 0 }]);
  };

  const updateMember = (index, field, value) => {
    const updatedMembers = members.map((member, i) =>
      i === index ? { ...member, [field]: value } : member
    );
    setMembers(updatedMembers);
  };

  const totalUnits = members.reduce((sum, member) => sum + Number(member.units), 0);
  const pricePerUnit = totalUnits ? fundTotal / totalUnits : 0;

  const formatCurrency = (num) =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);

  if (isLoading) {
    return (
      <div className="page-container">
        <h1>Fund Settings</h1>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <div className="status-badge loading">
            <span className="spinner"></span>
            Loading fund settings...
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <h1>Fund Settings</h1>

      {message && (
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
          <div className={`status-badge ${message.includes('success') || message.includes('saved') ? 'success' : 'error'}`}>
            {message}
          </div>
        </div>
      )}

      <div className="stats-grid">
        {/* Fund Parameters Card */}
        <div className="glass-card">
          <h3>Fund Parameters</h3>

          <div className="stat-item">
            <span className="stat-label">Fund Total</span>
            {isEditing ? (
              <input
                type="number"
                className="glass-input"
                style={{ width: '150px', textAlign: 'right' }}
                value={fundTotal}
                onChange={(e) => setFundTotal(Number(e.target.value))}
              />
            ) : (
              <span className="stat-value">{formatCurrency(fundTotal)}</span>
            )}
          </div>

          <div className="stat-item">
            <span className="stat-label">YTD Performance</span>
            {isEditing ? (
              <input
                type="number"
                className="glass-input"
                style={{ width: '100px', textAlign: 'right' }}
                value={ytdPerformance}
                onChange={(e) => setYtdPerformance(Number(e.target.value))}
              />
            ) : (
              <span className={`stat-value ${ytdPerformance >= 0 ? 'positive' : 'negative'}`}>
                {ytdPerformance.toFixed(2)}%
              </span>
            )}
          </div>

          <div className="stat-item">
            <span className="stat-label">Quarterly Fee</span>
            {isEditing ? (
              <input
                type="number"
                className="glass-input"
                style={{ width: '100px', textAlign: 'right' }}
                value={quarterlyFeePercent}
                onChange={(e) => setQuarterlyFeePercent(Number(e.target.value))}
              />
            ) : (
              <span className="stat-value">{quarterlyFeePercent}%</span>
            )}
          </div>

          <div className="stat-item">
            <span className="stat-label">Hurdle Rate</span>
            {isEditing ? (
              <input
                type="number"
                className="glass-input"
                style={{ width: '100px', textAlign: 'right' }}
                value={hurdleRate}
                onChange={(e) => setHurdleRate(Number(e.target.value))}
              />
            ) : (
              <span className="stat-value">{hurdleRate}%</span>
            )}
          </div>

          <div className="stat-item">
            <span className="stat-label">Carried Interest</span>
            {isEditing ? (
              <input
                type="number"
                className="glass-input"
                style={{ width: '100px', textAlign: 'right' }}
                value={carriedInterest}
                onChange={(e) => setCarriedInterest(Number(e.target.value))}
              />
            ) : (
              <span className="stat-value">{carriedInterest}%</span>
            )}
          </div>

          <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
            {!isEditing ? (
              <button className="btn-primary" onClick={handleEditClick}>Edit Settings</button>
            ) : (
              <>
                <button className="btn-primary" onClick={saveChanges}>Save Changes</button>
                <button className="btn-secondary" onClick={() => setIsEditing(false)}>Cancel</button>
              </>
            )}
          </div>
        </div>

        {/* Unit Pricing Card */}
        <div className="glass-card">
          <h3>Unit Pricing</h3>
          <div className="stat-item">
            <span className="stat-label">Total Units Outstanding</span>
            <span className="stat-value">{totalUnits.toLocaleString()}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Price Per Unit</span>
            <span className="stat-value">{formatCurrency(pricePerUnit)}</span>
          </div>
          <div className="stat-item">
            <span className="stat-label">Total Fund Value</span>
            <span className="stat-value">{formatCurrency(fundTotal)}</span>
          </div>
        </div>

        {/* Members Card */}
        <div className="glass-card" style={{ gridColumn: 'span 2' }}>
          <h3>Fund Members</h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="glass-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th style={{ textAlign: 'right' }}>Units</th>
                  <th style={{ textAlign: 'right' }}>Value</th>
                  <th style={{ textAlign: 'right' }}>Ownership</th>
                  <th style={{ textAlign: 'right' }}>Quarterly Fee</th>
                </tr>
              </thead>
              <tbody>
                {members.map((member, index) => (
                  <tr key={index}>
                    <td>
                      {isEditing ? (
                        <input
                          type="text"
                          className="glass-input"
                          style={{ width: '150px' }}
                          value={member.name}
                          onChange={(e) => updateMember(index, "name", e.target.value)}
                        />
                      ) : (
                        member.name
                      )}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                      {isEditing ? (
                        <input
                          type="number"
                          className="glass-input"
                          style={{ width: '100px', textAlign: 'right' }}
                          value={member.units}
                          onChange={(e) => updateMember(index, "units", Number(e.target.value))}
                        />
                      ) : (
                        member.units.toLocaleString()
                      )}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                      {formatCurrency(fundTotal * (member.units / (totalUnits || 1)))}
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }}>
                      {totalUnits ? ((member.units / totalUnits) * 100).toFixed(2) : "0.00"}%
                    </td>
                    <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', color: '#ff6b6b' }}>
                      {formatCurrency((quarterlyFeePercent / 100) * fundTotal * (member.units / (totalUnits || 1)))}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: 'rgba(255,255,255,0.05)' }}>
                  <td style={{ fontWeight: '600' }}>Total</td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: '600' }}>
                    {totalUnits.toLocaleString()}
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: '600' }}>
                    {formatCurrency(fundTotal)}
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: '600' }}>
                    100.00%
                  </td>
                  <td style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', fontWeight: '600', color: '#ff6b6b' }}>
                    {formatCurrency((quarterlyFeePercent / 100) * fundTotal)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
          {isEditing && (
            <button className="btn-secondary" style={{ marginTop: '16px' }} onClick={addMember}>
              + Add Member
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default FundSettings;
